<template>
  <div class="feed-list">
    <!-- Loading State -->
    <div v-if="loading" class="loading-container">
      <div class="loading-grid">
        <div v-for="n in 6" :key="n" class="loading-card">
          <div class="loading-skeleton loading-skeleton--header"></div>
          <div class="loading-skeleton loading-skeleton--title"></div>
          <div class="loading-skeleton loading-skeleton--title-short"></div>
          <div class="loading-skeleton loading-skeleton--meta"></div>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div v-else-if="!items || items.length === 0" class="empty-state">
      <div class="empty-state__icon">
        <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
          <polyline points="14,2 14,8 20,8"/>
          <line x1="16" y1="13" x2="8" y2="13"/>
          <line x1="16" y1="17" x2="8" y2="17"/>
          <polyline points="10,9 9,9 8,9"/>
        </svg>
      </div>
      <h3 class="empty-state__title">No hay contenido disponible</h3>
      <p class="empty-state__description">
        No se encontraron resultados con los filtros actuales. 
        Intenta ajustar la búsqueda o seleccionar más fuentes.
      </p>
    </div>

    <!-- News Grid / List -->
    <div v-else :class="layout === 'grid' ? 'news-grid' : 'news-list'">
      <slot name="pinned" />
      <component
        :is="getCardComponent(item)"
        v-for="item in items"
        :key="item.id || item.link"
        :item="item"
        @play-video="handlePlayVideo"
        @play-short="handlePlayShort"
        @open-pdf="handleOpenPdf"
      />
    </div>

    <!-- YouTube Player Modal -->
    <YouTubePlayer ref="youtubePlayer" />

    <!-- Shorts Player Modal -->
    <ShortsVideoPlayer ref="shortsPlayer" />

    <!-- PDF Viewer Modal -->
    <PdfViewerModal ref="pdfViewer" />
  </div>
</template>

<script>
import NewsCard from './NewsCard';
import NewsWithImageCard from './NewsWithImageCard';
import VideoCard from './VideoCard';
import PaperCard from './PaperCard';
import BlogCard from './BlogCard';
import YouTubePlayer from './YouTubePlayer';
import ShortsVideoPlayer from './ShortsVideoPlayer';
import PdfViewerModal from './PdfViewerModal';

export default {
  name: 'FeedList',
  components: {
    NewsCard,
    NewsWithImageCard,
    VideoCard,
    PaperCard,
    BlogCard,
    YouTubePlayer,
    ShortsVideoPlayer,
    PdfViewerModal
  },
  props: {
    items: {
      type: Array,
      required: true
    },
    loading: {
      type: Boolean,
      default: false
    },
    layout: {
      type: String,
      default: 'grid'
    },
  },
  methods: {
    getCardComponent(item) {
      // Blog: filas horizontales tipo artículo (HuggingFace)
      if (this.layout === 'blog') {
        return 'BlogCard';
      }
      // Papers: lista compacta con lector de PDF
      if (item.sourceType === 'pappers') {
        return 'PaperCard';
      }
      // Si es YouTube o TikTok, mostrar VideoCard
      if (item.sourceType === 'youtube' || item.sourceType === 'tiktok') {
        return 'VideoCard';
      }
      // Si tiene imagen (y no es video), mostrar NewsWithImageCard
      if (item.image) {
        return 'NewsWithImageCard';
      }
      // Por defecto, mostrar NewsCard
      return 'NewsCard';
    },
    handlePlayVideo(videoId, title) {
      // Abrir el reproductor de YouTube (videos normales)
      this.$refs.youtubePlayer.openPlayer(videoId, title);
    },
    handlePlayShort(videoId, title, videoType = 'youtube') {
      // Abrir el reproductor de Shorts (YouTube Shorts o TikTok)
      this.$refs.shortsPlayer.openPlayer(videoId, title, videoType);
    },
    handleOpenPdf(item) {
      // Abrir el lector de PDF embebido
      this.$refs.pdfViewer.open(item);
    }
  }
};
</script>

<style scoped>
/* Dark Theme - daily.dev style */
.feed-list {
  min-height: 400px;
}

/* Loading States */
.loading-container {
  padding: 20px 0;
}

.loading-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
}

.loading-card {
  background: #1c2127;
  border: 1px solid #2d333b;
  border-radius: 16px;
  padding: 20px;
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

.loading-skeleton {
  background: linear-gradient(90deg, #262c34 25%, #2d333b 50%, #262c34 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
  margin-bottom: 12px;
}

.loading-skeleton--header {
  height: 20px;
  width: 40%;
}

.loading-skeleton--title {
  height: 18px;
  width: 90%;
}

.loading-skeleton--title-short {
  height: 18px;
  width: 60%;
}

.loading-skeleton--meta {
  height: 14px;
  width: 30%;
}

@keyframes shimmer {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

/* Empty State - Dark Theme */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  text-align: center;
  color: #7d8694;
}

.empty-state__icon {
  margin-bottom: 20px;
  opacity: 0.5;
  color: #a0aec0;
}

.empty-state__title {
  font-size: 20px;
  font-weight: 600;
  color: #e1e7ef;
  margin: 0 0 8px 0;
}

.empty-state__description {
  font-size: 14px;
  line-height: 1.5;
  max-width: 400px;
  margin: 0;
  color: #7d8694;
}

/* News Grid */
.news-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
  padding: 20px 0;
}

/* News List (formato compacto, ej. Papers) */
.news-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 20px 0;
}

/* Responsive Design */
@media (max-width: 768px) {
  .loading-grid,
  .news-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }
  
  .loading-card {
    padding: 16px;
  }
  
  .empty-state {
    padding: 40px 20px;
  }
  
  .empty-state__title {
    font-size: 18px;
  }
}

@media (max-width: 480px) {
  .news-grid {
    gap: 12px;
  }
}
</style>